import React, { useEffect, useState, useRef, useMemo } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCSelectOptionsCard, RCCServicesApi, RCCSwitch, RCCTooltip } from "react-common-components";
import { IRefComponentSelectOptionsCard } from "react-common-components/dist/components/SelectOptionsCard/interface";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import FadedDiv from "src/components/Animations/fade";
import AppIcons from "src/components/AppIcons";
import ConfigsContainer from "src/components/ConfigsContainer";
import DateTimePicker from "src/components/DateTimePicker";
import InfiniteScrollWithoutFetch from "src/components/InfiniteScrollWithoutFetch";
import Loader from "src/components/Loader";
import PageTitle from "src/components/PageTitle";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import isValidDate from "src/utils/isValidDate";
import { IConfigsShowGoal } from "../Panel/components/ModalIndicatorChart/interface";
import ChartContent from "../PresentationPanel/components/ChartContent";
import { IListIndicatorsChart } from "../PresentationPanel/components/ListOfChartsIndicator/interface";
import { IConfigsChartPresentation } from "../PresentationPanel/components/PresentationContent/interface";
import Container from "./container";
import ModalIndicatorValuesOfPeriod from "../Panel/components/ModalValuesOfPeriod";
import { IRefActionsModalValuesOfPeriodIndicators } from "../Panel/components/ModalValuesOfPeriod/interface";

const PresentationPanelSelected: React.FC = () => {
	const { translate } = useTranslation();
	const params = useParams<{
        indicatorsId: string;
    }>();
	const { addToast } = RCCHookToast();

	const modalValuesOfPeriodIndicators = useRef<IRefActionsModalValuesOfPeriodIndicators>(null);

	const tendencyString = translate("Tendência");
	const [indicatorsId] = useState(params.indicatorsId);
	const [isKey] = useState(params.indicatorsId.startsWith("key="));

	const [loading, setLoading] = useState(true);
	const [list, setList] = useState<IListIndicatorsChart[]>([]);

	const [baseYear, setBaseYear] = useState(new Date().getFullYear().toString());

	// Configs
	const [showConfigs, setShowConfigs] = useState(false);
	const [toggleConfigShowGoals, setToogleConfigShowGoals] = useState<boolean>(false);
	const showValuesRef = useRef<IRefComponentSelectOptionsCard>(null);
	const { handleSubmit : handleSubmitConfigs, register: registerConfigs, control: controlConfigs } = useForm();
	const [configs, setConfigs] = useState<IConfigsChartPresentation>({ 
	} as IConfigsChartPresentation);
	const [configShowGoals, setConfigShowGoals] = useState<IConfigsShowGoal | undefined>(undefined);
	const resetConfigsFunction = () => {
		setConfigs({} as IConfigsChartPresentation);
		showValuesRef.current?.changeValue("1");
		setToogleConfigShowGoals(false);
		setConfigShowGoals({
			showGoalCommitmentInGraphic: undefined,
			showGoalCompetitivenessInGraphic: undefined,
			showGoalImprovementInGraphic: undefined
		});
	};
	const submitConfigs = async (data: any) => {
		setConfigs({ showAll: showValuesRef.current?.value === "1" ? undefined : showValuesRef.current?.value === "3" });
		setConfigShowGoals(!toggleConfigShowGoals ? undefined : {
			showGoalCommitmentInGraphic: data.showGoalCommitmentInGraphic,
			showGoalCompetitivenessInGraphic: data.showGoalCompetitivenessInGraphic,
			showGoalImprovementInGraphic: data.showGoalImprovementInGraphic
		});
	};
    
	const loadIndicators = async () => {
		try{
			setLoading(true);
            
			if(!isKey){
				const {data} = await RCCServicesApi.getRequest({
					method: "Indicators/ListPresentationPanelIndicators",
					urlParameters: {
						indicatorsId,
						baseYear
					}
				});
	
				setList(data as IListIndicatorsChart[]);
			}else{
				const {data} = await RCCServicesApi.getRequest({
					method: "IndicatorsPublic/ListPresentationPanelIndicators",
					urlParameters: {
						key: indicatorsId.replace("key=", "")
					}
				});
	
				setList(data as IListIndicatorsChart[]);
			}
			
			setLoading(false);
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao carregar os indicadores"), addToast);
		}
	};

	useEffect(() => {
		if(indicatorsId.trim().length === 0){
			addToast({
				color: "blue",
				description: translate("Selecione ao menos um indicador"),
				title: translate("Sucesso")
			});
			setLoading(false);
			return;
		}
		loadIndicators();
	}, [baseYear]);

	const ListCharts = useMemo(() => (
		<InfiniteScrollWithoutFetch 
			listElements={list}
			mapper={(listElement) => (<ChartContent 
				key={`${listElement.iqc001Id} chartContainerIndicator`}
				data={listElement}
				tendencyString={tendencyString}
				configs={configs}
				configShowGoals={configShowGoals}
				openValuesOfPeriodIndicator={(id: number, title: string, periodId: number, isPresentation?: boolean) => {
					if(!modalValuesOfPeriodIndicators.current)
						return;
					modalValuesOfPeriodIndicators.current?.actionButtonOpenValuesOfPeriodIndicators(id, title, periodId, () => {
						loadIndicators();
					}, isPresentation);
				}}
			/>)}
			defaultNumberOfElements={20}
			manuallyToggleShowMore 
		/>
	), [configs, configShowGoals, list]);

	if(loading)
		return <Loader />;
    
	if(!list || list.length === 0)
		return (
			<RCCAlert 
				title={translate("Atenção")}
				description={translate("Não foram encontrados dados")}
			/>
		);
        
	return (
		<>
			<Container isKey={isKey}>

				{/* Header -> Results + Buttons */}
				<PageTitle
					title={translate("Apresentação de indicadores")}
					icon={() => <AppIcons name="indicators" />}
				>
					<RCCTooltip
						text={`${translate("Configurações")} ${translate("gráficos")}`}
						color={showConfigs
						|| configs.showAll 
						|| configShowGoals?.showGoalCommitmentInGraphic !== undefined
						|| configShowGoals?.showGoalCompetitivenessInGraphic !== undefined
						|| configShowGoals?.showGoalImprovementInGraphic !== undefined ? "blue" : "grey"}
						position="left"
					>
						<RCCButton
							icon={{name: "settings", color: "#FFF", size: 16}}
							color={showConfigs 
							|| configs.showAll 
							|| configShowGoals?.showGoalCommitmentInGraphic !== undefined
							|| configShowGoals?.showGoalCompetitivenessInGraphic !== undefined
							|| configShowGoals?.showGoalImprovementInGraphic !== undefined ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowConfigs((prevState) => !prevState)}
						/>
					</RCCTooltip> 	
				</PageTitle>

				{/* Filters Configs Chart */}
				<ConfigsContainer 
					showConfigs={showConfigs}
					onSubmitConfigs={handleSubmitConfigs(submitConfigs)}
					resetConfigs={resetConfigsFunction}
				>	
					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={6}
							lg={4}
						>
							<RCCSelectOptionsCard 
								defaultValue="1"
								options={[{
									value: "1",
									label: translate("Conforme indicador")
								},{
									value: "2",
									label: translate("Esconder todos")
								},{
									value: "3",
									label: translate("Mostrar todos")
								}]}
								label={translate("Mostrar valor no gráfico")}
								ref={showValuesRef}
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={6}
							lg={4}
						>
							<RCCSwitch
								name="toggleConfigShowGoals"
								label={`${translate("Alterar metas que aparecem no gráfico")}`}
								register={registerConfigs}
								checked={toggleConfigShowGoals}
								onChange={() => setToogleConfigShowGoals((prevState) => !prevState)}
							/>							
						</RCCGrid>

					</RCCGrid>
					<RCCGrid container item>
						{toggleConfigShowGoals && (
							<FadedDiv styleInline=">label { flex-direction: row; } @media(min-width: 600px){ flex-direction: row; }">
								<RCCSwitch
									name="showGoalImprovementInGraphic"
									label={`${translate("Mostrar meta melhoria")}`}
									register={registerConfigs}
									defaultChecked
								/>
								<RCCSwitch
									name="showGoalCompetitivenessInGraphic"
									label={`${translate("Mostrar meta competitividade")}`}
									register={registerConfigs}
								/>
								<RCCSwitch
									name="showGoalCommitmentInGraphic"
									label={`${translate("Mostrar meta compromisso")}`}
									register={registerConfigs}
								/>
							</FadedDiv>
						)}
					</RCCGrid>
				</ConfigsContainer>

				{/* Groupers Select -> Origin -> Delayed */}
				{!isKey && (
					<RCCGrid
						container
						item
						spacing={1}
					>
		
						<RCCGrid
							container
							item
							xs={12}
							sm={4}
							md={3}
							lg={2}
						>	
							<DateTimePicker
								type="datePicker" 
								control={controlConfigs}
								name="baseYear"
								label={translate("Ano base")}
								defaultValue={new Date(Number(baseYear), 0, 1)}
								onChange={(e) => {
									if(isValidDate(e))
										setBaseYear(e.getFullYear().toString());
								}}
								props={{
									datePicker: {
										format: "yyyy",
										views: ["year"]
									}
								}}
							/>		
						</RCCGrid>

					</RCCGrid>			
				)}

				<RCCGrid container item direction="column">
					{ListCharts}
				</RCCGrid>
            
			</Container>

			<ModalIndicatorValuesOfPeriod ref={modalValuesOfPeriodIndicators} />
		</>
	);
};

export default PresentationPanelSelected;
